import { useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

// Cache for computed colors - only recompute when theme actually changes
let colorCache = { themeName: null, colors: null };

/**
 * Helper to resolve CSS variable values from DaisyUI
 * Called only when cache miss occurs
 */
const getComputedColor = (className, prop = 'color') => {
    try {
        const el = document.createElement('div');
        el.className = className;
        el.style.visibility = 'hidden';
        el.style.position = 'absolute';
        document.body.appendChild(el);
        const value = window.getComputedStyle(el)[prop];
        document.body.removeChild(el);
        return value || null;
    } catch {
        return null;
    }
};

/**
 * Get all theme colors - only recompute if theme changed
 */
const getThemeColors = (isDark) => {
    const themeName = isDark ? 'dark' : 'light';

    // Return cached if same theme
    if (colorCache.themeName === themeName && colorCache.colors) {
        return colorCache.colors;
    }

    // Compute new colors
    const colors = {
        text: getComputedColor('text-base-content') || (isDark ? '#e5e7eb' : '#374151'),
        grid: getComputedColor('border-base-300', 'borderColor') || (isDark ? '#374151' : '#e5e7eb'),
        background: getComputedColor('bg-base-100', 'backgroundColor') || (isDark ? '#1f2937' : '#ffffff'),
        success: getComputedColor('text-success') || '#22c55e',
        error: getComputedColor('text-error') || '#ef4444',
        primary: getComputedColor('text-primary') || '#3b82f6'
    };

    // Update cache
    colorCache = { themeName, colors };
    return colors;
};

/**
 * Hook to get ApexCharts theme options based on current DaisyUI theme
 * Provides consistent theming across all backtest charts
 * 
 * PERFORMANCE: Uses memoization + caching to prevent expensive DOM lookups on every render
 */
export const useApexTheme = () => {
    const { isDark } = useTheme();

    // Memoize everything based on isDark - prevents recalculation on every render
    const result = useMemo(() => {
        const colors = getThemeColors(isDark);

        const baseThemeOptions = {
            theme: {
                mode: isDark ? 'dark' : 'light'
            },
            chart: {
                background: 'transparent',
                foreColor: colors.text,
                // Disable animations for faster theme transitions
                animations: {
                    enabled: false
                }
            },
            grid: {
                borderColor: colors.grid,
                strokeDashArray: 4
            },
            xaxis: {
                labels: {
                    style: {
                        colors: colors.text
                    }
                },
                axisBorder: {
                    color: colors.grid
                },
                axisTicks: {
                    color: colors.grid
                }
            },
            yaxis: {
                labels: {
                    style: {
                        colors: colors.text
                    }
                }
            },
            tooltip: {
                theme: isDark ? 'dark' : 'light'
            },
            legend: {
                labels: {
                    colors: colors.text
                }
            }
        };

        return {
            isDark,
            baseThemeOptions,
            colors
        };
    }, [isDark]);

    return result;
};

/**
 * Merge base theme options with component-specific options
 * @param {Object} baseOptions - Base ApexCharts options from useApexTheme
 * @param {Object} componentOptions - Component-specific options
 * @returns {Object} Merged options
 */
export const mergeThemeOptions = (baseOptions, componentOptions) => {
    return {
        ...componentOptions,
        theme: {
            ...componentOptions.theme,
            ...baseOptions.theme
        },
        chart: {
            ...componentOptions.chart,
            ...baseOptions.chart
        },
        grid: {
            ...componentOptions.grid,
            ...baseOptions.grid
        },
        xaxis: {
            ...componentOptions.xaxis,
            labels: {
                ...componentOptions.xaxis?.labels,
                style: {
                    ...componentOptions.xaxis?.labels?.style,
                    ...baseOptions.xaxis.labels.style
                }
            },
            axisBorder: baseOptions.xaxis.axisBorder,
            axisTicks: baseOptions.xaxis.axisTicks
        },
        yaxis: Array.isArray(componentOptions.yaxis)
            ? componentOptions.yaxis.map(y => ({
                ...y,
                labels: {
                    ...y?.labels,
                    style: {
                        ...y?.labels?.style,
                        ...baseOptions.yaxis.labels.style
                    }
                }
            }))
            : {
                ...componentOptions.yaxis,
                labels: {
                    ...componentOptions.yaxis?.labels,
                    style: {
                        ...componentOptions.yaxis?.labels?.style,
                        ...baseOptions.yaxis.labels.style
                    }
                }
            },
        tooltip: {
            ...componentOptions.tooltip,
            ...baseOptions.tooltip
        },
        legend: {
            ...componentOptions.legend,
            labels: {
                ...componentOptions.legend?.labels,
                ...baseOptions.legend.labels
            }
        }
    };
};
